import React, { useState, useEffect } from 'react';
import TreatmentInpatientSelection from './TreatmentInpatientSelection';
import TreatmentInpatientChart from './TreatmentInpatientChart';
import { Tabs, Tab, Box, Snackbar, Alert, CircularProgress } from '@mui/material';
import { fetchRecordsPatientAllSurveysService } from '../../../services';
import { treatmentAdministrationService, TimeSlotName } from '../../../services/treatmentAdministration';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { useSelector } from 'react-redux';
import { formatDateToISO } from '../../../utils/dateUtils';
import { useSnackBarState } from '../../../hooks';
import { getCurrentResearcherUuid } from '../../../utils';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

interface TreatmentInpatientProps {
    mode: string;
    variant?: "standard" | "filled" | "outlined" | undefined;
    size?: string;
    language: string;
    typeMargin?: any;
    type: string;
    uuidPatient?: string;
    uuidInvestigation?: string;
    slaves?: object[];
    country?: string;
    template?: string;
    cancel?: () => void;
    updateElement?: (index: number, element: any) => void;
    elementSelected: (element: any) => void;
    error: boolean;
    initialTreatments?: Array<any>;
    fieldInfo?: any;
    uuidSurvey?: string;
    idSubmission?: number;
    onSaveToDatabase?: (treatmentData: any) => Promise<void>;
    onUpdateDatabase?: (treatmentData: any) => Promise<void>;
    onDeleteDatabase?: (treatmentData: any) => Promise<void>;
}

type TimeSlot = {
    status: "successful" | "not successful" | null;
    administrationId?: number,
    administeredBy?: string;
    createdAt?: string;
    reasonNotAdministered?: string;
};

const TreatmentInpatient: React.FC<TreatmentInpatientProps> = (props) => {
    const [tabValue, setTabValue] = useState(0);
    const [chartData, setChartData] = useState<Array<{
        drug: string;
        quantity: number;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        administrations: { [dateKey: string]: { timeSlots: { [slotName: string]: TimeSlot } } };
    }>>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    // Get investigation from Redux store to check permissions
    const investigation = useSelector((state: any) => {
        if (props.uuidInvestigation && state.investigations?.data) {
            return state.investigations.data.find((inv: any) => inv.uuid === props.uuidInvestigation);
        }
        return null;
    });

    // Check if user has permission to write administrations
    const hasWritePermission = investigation?.permissions?.includes(PERMISSION.TREATMENT_ADMINISTRATION_WRITE) ?? true; // Default to true if investigation not found

    /**
     * Fetches treatments from patient submissions to get treatmentPostId (postId from value object)
     */
    const fetchTreatmentPostIds = async (uuidInvestigation: string, uuidPatient: string): Promise<Record<string, number>> => {
        if (!uuidInvestigation || !uuidPatient) {
            return {};
        }

        try {
            const response = await fetchRecordsPatientAllSurveysService(uuidInvestigation, uuidPatient);
            
            // Extract treatmentPostIds from submissions - use postId/treatmentPostId from value object
            const treatmentPostIdMap: Record<string, number> = {};
            
            if (response && response.surveys) {
                response.surveys.forEach((survey: any) => {
                    if (survey.submissions) {
                        survey.submissions.forEach((submission: any) => {
                            if (submission.surveyRecords) {
                                submission.surveyRecords.forEach((record: any) => {
                                    if (record.surveyField && record.surveyField.type === "treatment_inpatient" && 
                                        record.value && Array.isArray(record.value)) {
                                        record.value.forEach((treatment: any) => {
                                            const drugName = treatment["treatment_inpatient"] || treatment.drug;
                                            if (drugName) {
                                                // Use postId or treatmentPostId from the treatment value object (per plan)
                                                // Fallback to record.id if postId is not in value object (temporary workaround)
                                                const postId = treatment.postId || treatment.treatmentPostId || record.id;
                                                if (postId) {
                                                    // Use drug name + route + frequency as key to match treatments
                                                    const route = treatment["treatment-route"] || "";
                                                    const frequency = treatment["treatment-frecuency"] || "";
                                                    const key = `${drugName}_${route}_${frequency}`;
                                                    treatmentPostIdMap[key] = postId;
                                                    // Also store by drug name alone for fallback
                                                    if (!treatmentPostIdMap[drugName]) {
                                                        treatmentPostIdMap[drugName] = postId;
                                                    }
                                                    // Store lowercase version for case-insensitive matching
                                                    const keyLower = key.toLowerCase();
                                                    if (!treatmentPostIdMap[keyLower]) {
                                                        treatmentPostIdMap[keyLower] = postId;
                                                    }
                                                } else {
                                                    // Treatment missing postId - will be handled during administration
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            }
            
            return treatmentPostIdMap;
        } catch (error) {
            return {};
        }
    };

    /**
     * Transforms treatment data from form format to chart format
     */
    const transformTreatmentsToChartData = (
        treatments: any[], 
        treatmentPostIdMap: Record<string, number> = {}
    ): Array<{
        drug: string;
        quantity: number;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        administrations: { [dateKey: string]: { timeSlots: { [slotName: string]: TimeSlot } } };
    }> => {
        if (!Array.isArray(treatments) || treatments.length === 0) {
            return [];
        }

        return treatments.map((treatment) => {
            // Extract treatment data - handle both form format and saved format
            const drug = treatment["treatment_inpatient"] || treatment.drug || "";
            const quantity = parseFloat(treatment["treatment-dose"] || treatment.quantity || 1);
            const frequency = treatment["treatment-frecuency"] || treatment.frequency || "";
            const route = treatment["treatment-route"] || treatment.route || "";
            
            // Determine if treatment is stopped
            const stoppedAt = treatment["treatment-stoppedAt"];
            const stopped = stoppedAt ? "1" : "0";
            
            // Get treatmentPostId from multiple sources - CHECK postId FIRST (as per plan)
            let treatmentPostId = treatment.postId ||                    // First: postId from value object (per plan)
                                treatment.treatmentPostId ||             // Second: treatmentPostId from value object
                                treatment.id ||                          // Third: id (backward compatibility)
                                treatment.surveyRecordId ||               // Fourth: surveyRecordId (backward compatibility)
                                null;
            
            // If not found, try to get from treatmentPostIdMap
            if (!treatmentPostId && treatmentPostIdMap) {
                const key = `${drug}_${route}_${frequency}`;
                // Try exact match first, then drug name only, then case-insensitive match
                treatmentPostId = treatmentPostIdMap[key] || 
                                 treatmentPostIdMap[drug] || 
                                 treatmentPostIdMap[key.toLowerCase()] ||
                                 treatmentPostIdMap[drug.toLowerCase()] ||
                                 null;
                
                if (treatmentPostId) {
                }
            }

            return {
                drug,
                quantity,
                frequency,
                route,
                stopped,
                treatmentPostId: treatmentPostId || undefined,
                administrations: {} // Empty initially, will be populated by chart component from API
            };
        });
    };

    // Transform initialTreatments to chart data when component mounts or treatments change
    useEffect(() => {
        const updateChartData = async () => {
            const treatments = props.initialTreatments || [];
            
            if (treatments.length === 0) {
                setChartData([]);
                setInitialLoading(false);
                return;
            }


            // Fetch treatmentPostIds if we have patient and investigation info
            let treatmentPostIdMap: Record<string, number> = {};
            if (props.uuidInvestigation && props.uuidPatient) {
                treatmentPostIdMap = await fetchTreatmentPostIds(props.uuidInvestigation, props.uuidPatient);
            }

            // Transform treatments to chart data format
            const transformedData = transformTreatmentsToChartData(treatments, treatmentPostIdMap);
            
            // Transformed chart data ready
            
            setChartData(transformedData);
        };

        updateChartData();
    }, [props.initialTreatments, props.uuidInvestigation, props.uuidPatient]);

    // Load existing administrations from server
    useEffect(() => {
        const loadExistingAdministrations = async () => {
            if (!props.uuidPatient || !props.uuidInvestigation || chartData.length === 0) {
                setInitialLoading(false);
                return;
            }

            try {
                const response = await treatmentAdministrationService.getPatientAdministrations(
                    props.uuidInvestigation, 
                    props.uuidPatient
                );

                // Response is already an array of AdministrationResponse
                const existingAdministrations = Array.isArray(response) ? response : [];
                
                // Merge existing administrations with treatment data
                const updatedData = [...chartData];
                existingAdministrations.forEach((admin: any) => {
                    // Find the treatment in our data by treatmentPostId
                    const treatmentPostId = admin.sourcePost?.id;
                    const treatmentIndex = updatedData.findIndex(t => t.treatmentPostId === treatmentPostId);
                    
                    if (treatmentIndex >= 0) {
                        // Parse administrationDate from fieldValues or use relatedAt
                        const adminDateStr = admin.fieldValues?.administrationDate || admin.relatedAt;
                        const adminDate = new Date(adminDateStr);
                        const dateKey = formatDateToISO(adminDate); // YYYY-MM-DD format
                        
                        // Get timeSlot from fieldValues (from selected row)
                        const timeSlot = (admin.fieldValues?.timeSlot || 'morning') as TimeSlotName;

                        // Initialize administration structure if needed
                        if (!updatedData[treatmentIndex].administrations[dateKey]) {
                            updatedData[treatmentIndex].administrations[dateKey] = { timeSlots: {} };
                        }
                        if (!updatedData[treatmentIndex].administrations[dateKey].timeSlots[timeSlot]) {
                            updatedData[treatmentIndex].administrations[dateKey].timeSlots[timeSlot] = {
                                status: null
                            };
                        }

                        // Update with server data
                        const wasAdministered = admin.fieldValues?.wasAdministered === "true";
                        
                        updatedData[treatmentIndex].administrations[dateKey].timeSlots[timeSlot] = {
                            status: wasAdministered ? 'successful' : 'not successful',
                            administrationId: admin.id,
                            administeredBy: admin.fieldValues?.administeredBy || admin.administeredBy,
                            createdAt: admin.createdAt,
                            reasonNotAdministered: admin.fieldValues?.reasonNotAdministered
                        };
                    }
                });

                setChartData(updatedData);
                
            } catch (error: any) {
                // Could not load existing administrations - continue with empty data
            } finally {
                setInitialLoading(false);
            }
        };

        loadExistingAdministrations();
    }, [props.uuidPatient, props.uuidInvestigation, chartData.length]);

    // Handle tab change (Inpatients & Inpatient Medication Chart)
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    /**
     * Shows user feedback message
     */
    const showMessage = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
        setShowSnackbar({
            show: true,
            message,
            severity: severity as 'success' | 'error' | 'warning' | 'info'
        });
    };

    // Generic helper to update a cell's property in the nested data structure
    const updateCellProperty = (
        drugIndex: number, 
        dateKey: string,
        timeSlotName: TimeSlotName, 
        updates: { status?: "successful" | "not successful" | null; administrationId?: number; reasonNotAdministered?: string; administeredBy?: string; createdAt?: string; }
    ) => {
        setChartData(prevData => {
            const newData = [...prevData];
            const drug = { ...newData[drugIndex] };
            const administrations = { ...drug.administrations };
            
            // Ensure the administration for this date exists
            if (!administrations[dateKey]) {
                administrations[dateKey] = { timeSlots: {} };
            }
            
            const dayAdmin = { ...administrations[dateKey] };
            const timeSlots = {...(dayAdmin.timeSlots || {})};
            
            // Ensure the timeSlot exists
            if (!timeSlots[timeSlotName]) {
                timeSlots[timeSlotName] = { status: null };
            }
            
            // Apply the updates
            timeSlots[timeSlotName] = {
                ...timeSlots[timeSlotName],
                ...updates
            };
            
            // Rebuild the structure
            dayAdmin.timeSlots = timeSlots;
            administrations[dateKey] = dayAdmin;
            drug.administrations = administrations;
            newData[drugIndex] = drug;
            
            return newData;
        });
    };

    /**
     * Enhanced status update that persists to backend
     */
    const handleStatusUpdate = async (
        drugIndex: number, 
        dateKey: string,
        timeSlotName: TimeSlotName, 
        status: "successful" | "not successful" | null,
        reasonNotAdministered?: string
    ) => {
        // Check permissions
        if (!hasWritePermission) {
            showMessage('You do not have permission to record administrations', 'error');
            return;
        }

        const treatment = chartData[drugIndex];
        
        if (!treatment?.treatmentPostId) {
            // Missing treatmentPostId - administration cannot be saved
            if (false) { // Debug flag
            console.error('❌ Missing treatmentPostId:', {
                drugIndex,
                treatment: treatment ? {
                    drug: treatment.drug,
                    route: treatment.route,
                    frequency: treatment.frequency,
                    treatmentPostId: treatment.treatmentPostId
                } : 'treatment is null',
                allTreatments: chartData.map(t => ({
                    drug: t.drug,
                    treatmentPostId: t.treatmentPostId
                }))
            });
            }
            showMessage(`Treatment ID not found for ${treatment?.drug || 'unknown drug'}. Cannot save administration.`, 'error');
            return;
        }

        setLoading(true);

        try {
            // dateKey is already in YYYY-MM-DD format
            const administrationDate = dateKey;

            if (status === null) {

                const currentTimeSlot = treatment.administrations[dateKey]?.timeSlots[timeSlotName];
                const administrationId = currentTimeSlot?.administrationId;

                if (!administrationId) {
                    // No administration ID means it was never saved to backend, just update locally
                    updateCellProperty(drugIndex, dateKey, timeSlotName, { status: null });
                    showMessage('Administration record removed', 'warning');
                    setLoading(false);
                    return;
                }

                // Delete from backend
                try {
                    await treatmentAdministrationService.deleteAdministration(
                        props.uuidInvestigation || '',
                        treatment.treatmentPostId!,
                        administrationId
                    );
                    
                    // Update locally after successful deletion
                    updateCellProperty(drugIndex, dateKey, timeSlotName, { status: null });
                    showMessage('✅ Administration record deleted successfully', 'success');
                } catch (error: any) {
                    console.error('Failed to delete administration:', error);
                    
                    // Show backend error message directly
                    const errorMessage = error.message || 'Failed to delete administration record';
                    showMessage(errorMessage, 'error');
                } finally {
                    setLoading(false);
                }
                return;

            }

            // Prepare administration record - use administrationDate (YYYY-MM-DD) and timeSlot from selected row
            const administrationData = {
                treatmentPostId: treatment.treatmentPostId,
                patientUuid: props.uuidPatient || '',
                wasAdministered: status === "successful",
                administrationDate: administrationDate, // YYYY-MM-DD format
                timeSlot: timeSlotName, // From selected row
                reasonNotAdministered: status === "not successful" ? (reasonNotAdministered || undefined) : undefined,
                administeredBy: getCurrentResearcherUuid() as string, // Can be added later if needed
            };


            // Save to backend
            const response = await treatmentAdministrationService.recordAdministration(
                props.uuidInvestigation || '', 
                administrationData
            );

            // Update local state
            updateCellProperty(drugIndex, dateKey, timeSlotName, { status, administrationId: response.id, reasonNotAdministered: status === "not successful" ? reasonNotAdministered: undefined, administeredBy: getCurrentResearcherUuid() as string, createdAt: response.createdAt});
            
            showMessage(
                status === "successful" 
                    ? '✅ Administration recorded successfully' 
                    : '⚠️ Non-administration recorded',
                'success'
            );
            
        } catch (error: any) {
            console.error('Failed to save administration:', error);
            
            let errorMessage = 'Failed to save administration record';
            if (error.response?.data?.errorCode) {
                const errorCode = error.response.data.errorCode;
                let apiErrorMessage = error.response.data.errorMessage;
                // Handle errorMessage as string or object
                if (typeof apiErrorMessage === 'object' && apiErrorMessage !== null) {
                    apiErrorMessage = JSON.stringify(apiErrorMessage);
                }
                errorMessage = apiErrorMessage || `Error ${errorCode}: ${treatmentAdministrationService.ERROR_MESSAGES?.[errorCode] || 'Unknown error'}`;
            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to record administrations';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error - please try again later';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            showMessage(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };
  
    if(props.initialTreatments?.length === 0) {
        return <TreatmentInpatientSelection 
                {...props}
                initialTreatments={props.initialTreatments}
                fieldInfo={props.fieldInfo}
                uuidSurvey={props.uuidSurvey}
                uuidPatient={props.uuidPatient}
                uuidInvestigation={props.uuidInvestigation}
                idSubmission={props.idSubmission}
                onSaveToDatabase={props.onSaveToDatabase}
                onUpdateDatabase={props.onUpdateDatabase}
                onDeleteDatabase={props.onDeleteDatabase}
            />
    }
    return (
        <Box>
        <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable" 
            scrollButtons="auto"
            aria-label="treatment tabs"
        >
            <Tab label={<Translate id="hospital.treatment-inpatient-medication-orders"/>} id="tab-0" aria-controls="tabpanel-0" />
            <Tab label={<Translate id="hospital.treatment-inpatient-medication-chart" />} id="tab-1" aria-controls="tabpanel-1" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
            <TreatmentInpatientSelection 
                {...props}
                initialTreatments={props.initialTreatments}
                fieldInfo={props.fieldInfo}
                uuidSurvey={props.uuidSurvey}
                uuidPatient={props.uuidPatient}
                uuidInvestigation={props.uuidInvestigation}
                idSubmission={props.idSubmission}
                onSaveToDatabase={props.onSaveToDatabase}
                onUpdateDatabase={props.onUpdateDatabase}
                onDeleteDatabase={props.onDeleteDatabase}
            />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
            {initialLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" p={4}>
                    <CircularProgress size={40} />
                    <Box ml={2}>Loading existing administrations...</Box>
                </Box>
            ) : (
                <Box position="relative">
                    <TreatmentInpatientChart
                        data={chartData}
                        patientUuid={props.uuidPatient}
                        uuidInvestigation={props.uuidInvestigation}
                        language={props.language}
                        onStatusUpdate={handleStatusUpdate}
                    />
                    
                    {/* Loading overlay for API calls */}
                    {loading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="rgba(255, 255, 255, 0.7)"
                            zIndex={1000}
                        >
                            <CircularProgress size={40} />
                        </Box>
                    )}
                </Box>
            )}
        </TabPanel>
        
        {/* Snackbar for feedback */}
        <Snackbar 
            open={showSnackbar.show} 
            autoHideDuration={4000} 
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert 
                onClose={handleCloseSnackbar} 
                severity={showSnackbar.severity || 'success'} 
                sx={{ width: '100%' }}
            >
                {showSnackbar.message}
            </Alert>
        </Snackbar>
    </Box>
    );
};

export default TreatmentInpatient;
